import React, { useCallback, useRef, useState } from 'react';
import { Play } from 'lucide-react';
import SchoolMediaLightbox from './SchoolMediaLightbox';

// A plate wall rather than a row of equal cards: plates are laid on a six
// column grid in a repeating rhythm of wide/narrow pairs, so no two rows read
// the same. A trailing odd plate runs the full width.
const PLATE_SHAPES = ['a', 'b', 'c', 'd', 'e', 'f'];

const shapeFor = (index, total) => {
  const isTrailingOdd = index === total - 1 && index % 2 === 0;
  return isTrailingOdd ? 'wide' : PLATE_SHAPES[index % PLATE_SHAPES.length];
};

/**
 * The school's photographs and video, sorted by `displayOrder` upstream. Plates
 * are buttons, so the wall is fully keyboard operable, and each opens the
 * lightbox. Renders nothing when the school has no media.
 */
const SchoolMediaGallery = ({ profile }) => {
  const { media, schoolName } = profile;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [failedIds, setFailedIds] = useState(() => new Set());
  const plateRefs = useRef({});

  const markFailed = useCallback((id) => {
    setFailedIds((previous) => {
      if (previous.has(id)) return previous;
      const next = new Set(previous);
      next.add(id);
      return next;
    });
  }, []);

  const closeLightbox = useCallback(() => {
    const openedId = lightboxIndex !== null ? media[lightboxIndex]?.id : null;
    setLightboxIndex(null);

    // Send focus back to the plate the viewer was opened from.
    const trigger = openedId ? plateRefs.current[openedId] : null;
    if (trigger) trigger.focus();
  }, [lightboxIndex, media]);

  if (!media || media.length === 0) return null;

  const photoCount = media.filter((item) => !item.isVideo).length;
  const videoCount = media.length - photoCount;

  return (
    <section className="sp-section" id="gallery" aria-labelledby="sp-gallery-heading">
      <div className="sp-shell sp-editorial">
        <div className="sp-rail">
          <p className="sp-label">Gallery</p>
          <p className="sp-gallery-count">
            {photoCount > 0 && `${photoCount} photograph${photoCount === 1 ? '' : 's'}`}
            {photoCount > 0 && videoCount > 0 && ', '}
            {videoCount > 0 && `${videoCount} video${videoCount === 1 ? '' : 's'}`}
          </p>
        </div>

        <div>
          <h2 className="sp-display sp-display-m" id="sp-gallery-heading">
            Life at {schoolName}
          </h2>
          <p className="sp-standfirst sp-gallery-lead">
            A look around our campus, classrooms and school community.
          </p>

          <ul className="sp-plates">
            {media.map((item, index) => {
              const hasFailed = failedIds.has(item.id);
              const previewUrl = item.isVideo ? item.thumbnailUrl : item.mediaUrl;
              const showFallback = hasFailed || !previewUrl;

              return (
                <li
                  className="sp-plate"
                  data-shape={shapeFor(index, media.length)}
                  key={item.id}
                >
                  <button
                    type="button"
                    className="sp-plate-button"
                    ref={(node) => {
                      plateRefs.current[item.id] = node;
                    }}
                    onClick={() => setLightboxIndex(index)}
                    aria-label={`Open ${item.isVideo ? 'video' : 'photo'} ${index + 1} of ${
                      media.length
                    } in full view`}
                  >
                    {showFallback ? (
                      <span className="sp-plate-fallback">
                        {item.isVideo ? 'Video unavailable' : 'Photograph unavailable'}
                      </span>
                    ) : (
                      <img
                        className="sp-plate-photo"
                        src={previewUrl}
                        alt={item.alt}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                        onError={() => markFailed(item.id)}
                      />
                    )}

                    {item.isVideo && (
                      <span className="sp-plate-play" aria-hidden="true">
                        <Play size={16} strokeWidth={1.75} fill="currentColor" />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {lightboxIndex !== null && (
        <SchoolMediaLightbox
          items={media}
          activeIndex={lightboxIndex}
          schoolName={schoolName}
          onClose={closeLightbox}
          onNavigate={setLightboxIndex}
        />
      )}

      <style>{`
        .sp-gallery-count {
          margin: 0.6rem 0 0 0;
          font-size: 0.9rem;
          color: var(--sp-ink-muted);
          font-variant-numeric: tabular-nums;
        }

        .sp-gallery-lead {
          margin-top: 1.25rem;
        }

        .sp-plates {
          list-style: none;
          margin: 2.25rem 0 0 0;
          padding: 0;
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          grid-auto-rows: clamp(74px, 8.5vw, 118px);
          grid-auto-flow: dense;
          gap: clamp(0.5rem, 1vw, 0.9rem);
        }

        .sp-plate[data-shape='a'] { grid-column: span 4; grid-row: span 3; }
        .sp-plate[data-shape='b'] { grid-column: span 2; grid-row: span 3; }
        .sp-plate[data-shape='c'] { grid-column: span 2; grid-row: span 2; }
        .sp-plate[data-shape='d'] { grid-column: span 4; grid-row: span 2; }
        .sp-plate[data-shape='e'] { grid-column: span 3; grid-row: span 3; }
        .sp-plate[data-shape='f'] { grid-column: span 3; grid-row: span 3; }
        .sp-plate[data-shape='wide'] { grid-column: span 6; grid-row: span 2; }

        .sp-plate-button {
          position: relative;
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: 1px solid var(--sp-rule);
          border-radius: 2px;
          overflow: hidden;
          background: var(--sp-paper-raised);
          cursor: pointer;
          transition: border-color 0.25s ease, box-shadow 0.25s ease;
        }

        .sp-plate-button:hover {
          border-color: var(--sp-accent-ink);
          box-shadow: 0 12px 30px -18px var(--sp-shadow-tint);
        }

        .sp-plate-button:focus-visible {
          outline: 2px solid var(--sp-accent-ink);
          outline-offset: 3px;
        }

        .sp-plate-photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.5s cubic-bezier(0.2, 0.7, 0.3, 1);
        }

        .sp-plate-button:hover .sp-plate-photo {
          transform: scale(1.03);
        }

        .sp-plate-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          box-sizing: border-box;
          text-align: center;
          font-size: 0.82rem;
          color: var(--sp-ink-muted);
        }

        /* Squared play mark, sat in the corner rather than centred on the image. */
        .sp-plate-play {
          position: absolute;
          left: 0.75rem;
          bottom: 0.75rem;
          width: 38px;
          height: 38px;
          border-radius: 2px;
          display: flex;
          align-items: center;
          justify-content: center;
          /* -strong is always dark enough for a white mark, whatever the brand. */
          background: var(--sp-accent-strong);
          color: #ffffff;
          /* Optical nudge: a triangle centres left of the box's true middle. */
          padding-left: 2px;
        }

        @media (max-width: 940px) {
          .sp-plates {
            grid-template-columns: repeat(2, 1fr);
            grid-auto-rows: auto;
          }

          .sp-plate,
          .sp-plate[data-shape] {
            grid-column: auto;
            grid-row: auto;
            aspect-ratio: 4 / 3;
          }

          .sp-plate:first-child,
          .sp-plate[data-shape='wide'] {
            grid-column: span 2;
            aspect-ratio: 16 / 9;
          }
        }

        @media (max-width: 560px) {
          .sp-plates {
            grid-template-columns: 1fr;
          }

          .sp-plate,
          .sp-plate[data-shape],
          .sp-plate:first-child,
          .sp-plate[data-shape='wide'] {
            grid-column: auto;
            aspect-ratio: 4 / 3;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sp-plate-button:hover .sp-plate-photo {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
};

export default SchoolMediaGallery;
